import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Plus, 
  Edit, 
  Trash2, 
  User,
  Search,
  Phone,
  Mail,
  Calendar,
  Video,
  Building,
  Clock,
  Award,
  Stethoscope
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const SPECIALTIES = [
  { value: 'general_practice', label: 'General Practice' },
  { value: 'family_medicine', label: 'Family Medicine' },
  { value: 'internal_medicine', label: 'Internal Medicine' },
  { value: 'pediatrics', label: 'Pediatrics' },
  { value: 'cardiology', label: 'Cardiology' },
  { value: 'dermatology', label: 'Dermatology' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'orthopedics', label: 'Orthopedics' },
  { value: 'psychiatry', label: 'Psychiatry' },
  { value: 'gynecology', label: 'Gynecology' },
  { value: 'urology', label: 'Urology' },
  { value: 'ophthalmology', label: 'Ophthalmology' },
  { value: 'ent', label: 'ENT' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'other', label: 'Other' }
];

const TITLES = [
  { value: 'MD', label: 'MD (Doctor of Medicine)' },
  { value: 'DO', label: 'DO (Doctor of Osteopathy)' },
  { value: 'DDS', label: 'DDS (Doctor of Dental Surgery)' },
  { value: 'DMD', label: 'DMD (Doctor of Medicine in Dentistry)' },
  { value: 'PhD', label: 'PhD (Doctor of Philosophy)' },
  { value: 'NP', label: 'NP (Nurse Practitioner)' },
  { value: 'PA', label: 'PA (Physician Assistant)' },
  { value: 'RN', label: 'RN (Registered Nurse)' },
  { value: 'PT', label: 'PT (Physical Therapist)' },
  { value: 'OD', label: 'OD (Doctor of Optometry)' }
];

// Helper function to format provider display name avoiding title duplication
const formatProviderName = (title, fullName) => {
  if (!fullName) return '';
  
  // List of common title prefixes to check for duplication
  const titlePrefixes = ['Dr.', 'Dr', 'MD', 'DO', 'PhD', 'NP', 'PA', 'RN', 'PT', 'OD', 'DDS', 'DMD'];
  
  // Check if the name already starts with a title
  const nameLower = fullName.toLowerCase().trim();
  const startsWithTitle = titlePrefixes.some(prefix => 
    nameLower.startsWith(prefix.toLowerCase() + ' ') || 
    nameLower.startsWith(prefix.toLowerCase() + '.')
  );
  
  // If name already has a title, just return the name
  if (startsWithTitle) {
    return fullName;
  }
  
  // Otherwise, prepend the title
  return title ? `${title} ${fullName}` : fullName;
};

const ProvidersManagement = () => {
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    title: 'MD',
    email: '',
    phone: '',
    specialty: 'general_practice',
    license_number: '',
    bio: '',
    services: [],
    accepts_telemedicine: true,
    accepts_in_person: true,
    default_appointment_duration: 30
  });

  const [schedule, setSchedule] = useState({
    monday: { is_working: true, slots: [{ start_time: '09:00', end_time: '17:00' }] },
    tuesday: { is_working: true, slots: [{ start_time: '09:00', end_time: '17:00' }] },
    wednesday: { is_working: true, slots: [{ start_time: '09:00', end_time: '17:00' }] },
    thursday: { is_working: true, slots: [{ start_time: '09:00', end_time: '17:00' }] },
    friday: { is_working: true, slots: [{ start_time: '09:00', end_time: '17:00' }] },
    saturday: { is_working: false, slots: [] },
    sunday: { is_working: false, slots: [] }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [providersRes, servicesRes] = await Promise.all([
        axios.get(`${API}/providers`),
        axios.get(`${API}/clinic/services`)
      ]);
      setProviders(providersRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading providers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await axios.put(`${API}/providers/${editingProvider.id}`, formData);
        toast.success('Provider updated successfully');
      } else {
        await axios.post(`${API}/providers`, formData);
        toast.success('Provider created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving provider');
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      full_name: provider.full_name,
      title: provider.title || 'MD',
      email: provider.email || '',
      phone: provider.phone || '',
      specialty: provider.specialty,
      license_number: provider.license_number || '',
      bio: provider.bio || '',
      services: provider.services || [],
      accepts_telemedicine: provider.accepts_telemedicine,
      accepts_in_person: provider.accepts_in_person,
      default_appointment_duration: provider.default_appointment_duration || 30
    });
    setShowModal(true);
  };

  const handleDelete = async (providerId) => {
    if (!confirm('Are you sure you want to deactivate this provider?')) return;
    try {
      await axios.delete(`${API}/providers/${providerId}`);
      toast.success('Provider deactivated');
      fetchData();
    } catch (error) {
      toast.error('Error deactivating provider');
    }
  };

  const handleOpenSchedule = async (provider) => {
    setSelectedProvider(provider);
    try {
      const response = await axios.get(`${API}/providers/${provider.id}/schedule`);
      if (response.data) {
        setSchedule({
          monday: response.data.monday || { is_working: false, slots: [] },
          tuesday: response.data.tuesday || { is_working: false, slots: [] },
          wednesday: response.data.wednesday || { is_working: false, slots: [] },
          thursday: response.data.thursday || { is_working: false, slots: [] },
          friday: response.data.friday || { is_working: false, slots: [] },
          saturday: response.data.saturday || { is_working: false, slots: [] },
          sunday: response.data.sunday || { is_working: false, slots: [] }
        });
      }
    } catch (error) {
      // Use default schedule if none exists
    }
    setShowScheduleModal(true);
  };

  const handleSaveSchedule = async () => {
    try {
      await axios.put(`${API}/providers/${selectedProvider.id}/schedule`, schedule);
      toast.success('Schedule saved successfully');
      setShowScheduleModal(false);
    } catch (error) {
      toast.error('Error saving schedule');
    }
  };

  const resetForm = () => {
    setEditingProvider(null);
    setFormData({
      full_name: '',
      title: 'MD',
      email: '',
      phone: '',
      specialty: 'general_practice',
      license_number: '',
      bio: '',
      services: [],
      accepts_telemedicine: true,
      accepts_in_person: true,
      default_appointment_duration: 30
    });
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const updateDaySchedule = (day, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const filteredProviders = providers.filter(provider =>
    provider.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (provider.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSpecialtyLabel = (specialty) => {
    return SPECIALTIES.find(s => s.value === specialty)?.label || specialty;
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const DAYS = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Healthcare Providers</h1>
          <p className="text-gray-600 mt-1">Manage doctors and specialists at your clinic</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-4 w-4 mr-2" />
          New Provider
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Providers</p>
                <p className="text-2xl font-bold text-gray-900">{providers.length}</p>
              </div>
              <User className="h-8 w-8 text-teal-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {providers.filter(p => p.is_active).length}
                </p>
              </div>
              <Award className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Telemedicine</p>
                <p className="text-2xl font-bold text-blue-600">
                  {providers.filter(p => p.accepts_telemedicine).length}
                </p>
              </div>
              <Video className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In-Person</p>
                <p className="text-2xl font-bold text-purple-600">
                  {providers.filter(p => p.accepts_in_person).length}
                </p>
              </div>
              <Building className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Providers Grid */}
      {filteredProviders.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No se encontraron proveedores</p>
              <Button onClick={() => { resetForm(); setShowModal(true); }} variant="link" className="mt-2">
                Agregar el primer proveedor
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProviders.map((provider) => (
            <Card key={provider.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={provider.photo_url} />
                    <AvatarFallback className="bg-gradient-to-br from-teal-500 to-blue-500 text-white text-lg">
                      {getInitials(provider.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {formatProviderName(provider.title, provider.full_name)}
                        </h3>
                        <p className="text-sm text-teal-600">{getSpecialtyLabel(provider.specialty)}</p>
                      </div>
                      <Badge variant={provider.is_active ? 'default' : 'secondary'}>
                        {provider.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {provider.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4" />
                      {provider.email}
                    </div>
                  )}
                  {provider.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4" />
                      {provider.phone}
                    </div>
                  )}
                  {provider.license_number && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Award className="h-4 w-4" />
                      Lic: {provider.license_number}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex gap-2">
                  {provider.accepts_in_person && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Building className="h-3 w-3" /> In-Person
                    </Badge>
                  )}
                  {provider.accepts_telemedicine && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Video className="h-3 w-3" /> Telemedicine
                    </Badge>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t flex justify-between">
                  <Button variant="outline" size="sm" onClick={() => handleOpenSchedule(provider)}>
                    <Calendar className="h-4 w-4 mr-1" /> Schedule
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(provider)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(provider.id)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Provider Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProvider ? 'Edit Provider' : 'New Provider'}</DialogTitle>
            <DialogDescription>
              {editingProvider ? 'Modify the provider information' : 'Add a new provider to your clinic'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <div className="relative">
                  <Input
                    id="title"
                    list="title-options"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Select or type a title"
                    className="w-full"
                  />
                  <datalist id="title-options">
                    {TITLES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </datalist>
                </div>
                <p className="text-xs text-gray-500 mt-1">Select from list or enter custom title</p>
              </div>
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div>
                <Label htmlFor="specialty">Specialty *</Label>
                <Select value={formData.specialty} onValueChange={(v) => setFormData({ ...formData, specialty: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="license_number">License Number</Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  placeholder="Professional license number"
                />
              </div>
              <div>
                <Label htmlFor="duration">Appointment Duration (min)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="5"
                  max="120"
                  value={formData.default_appointment_duration}
                  onChange={(e) => setFormData({ ...formData, default_appointment_duration: parseInt(e.target.value) })}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="bio">Biography</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief description of the provider..."
                  rows={3}
                />
              </div>
              
              {/* Modality */}
              <div className="col-span-2 grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-gray-600" />
                    <div>
                      <Label>In-Person</Label>
                      <p className="text-xs text-gray-500">Accepts in-person appointments</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.accepts_in_person}
                    onCheckedChange={(v) => setFormData({ ...formData, accepts_in_person: v })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Video className="h-5 w-5 text-gray-600" />
                    <div>
                      <Label>Telemedicine</Label>
                      <p className="text-xs text-gray-500">Accepts virtual consultations</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.accepts_telemedicine}
                    onCheckedChange={(v) => setFormData({ ...formData, accepts_telemedicine: v })}
                  />
                </div>
              </div>

              {/* Services */}
              {services.length > 0 && (
                <div className="col-span-2">
                  <Label>Services Offered</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg">
                    {services.map((service) => (
                      <div key={service.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`service-${service.id}`}
                          checked={formData.services.includes(service.id)}
                          onCheckedChange={() => handleServiceToggle(service.id)}
                        />
                        <label
                          htmlFor={`service-${service.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {service.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingProvider ? 'Save Changes' : 'Create Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Schedule for {formatProviderName(selectedProvider?.title, selectedProvider?.full_name)}</DialogTitle>
            <DialogDescription>Set the working hours</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {DAYS.map((day) => (
              <div key={day.key} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="w-24">
                  <Label className="font-medium">{day.label}</Label>
                </div>
                <Switch
                  checked={schedule[day.key]?.is_working || false}
                  onCheckedChange={(v) => updateDaySchedule(day.key, 'is_working', v)}
                />
                {schedule[day.key]?.is_working && (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      type="time"
                      value={schedule[day.key]?.slots?.[0]?.start_time || '09:00'}
                      onChange={(e) => updateDaySchedule(day.key, 'slots', [{ ...schedule[day.key].slots[0], start_time: e.target.value }])}
                      className="w-32"
                    />
                    <span className="text-gray-500">a</span>
                    <Input
                      type="time"
                      value={schedule[day.key]?.slots?.[0]?.end_time || '17:00'}
                      onChange={(e) => updateDaySchedule(day.key, 'slots', [{ ...schedule[day.key].slots[0], end_time: e.target.value }])}
                      className="w-32"
                    />
                  </div>
                )}
                {!schedule[day.key]?.is_working && (
                  <span className="text-sm text-gray-500">Not working</span>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSchedule} className="bg-teal-600 hover:bg-teal-700">
              Save Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProvidersManagement;
